import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import CityDropdown from '../../components/CityDropdown';

const EditRentProperty = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const navigate = useNavigate();

  // State for form data
  const [formData, setFormData] = useState({
    Property_Type: '',
    Property_Title: '',
    Property_Description: '',
    Property_Location: '',
    City: '',
    Rent_Price: '',
    No_of_Bed: '',
    No_of_Bathrooms: '',
    No_of_Kitchen: '',
    Floor_Number: '',
    No_of_Floors: '',
    Contact_Number: '',
    Contact_Name: '',
    images: [],
    Yard: '',
    Marla: '',
    Kanal: '',
    Acre: '',
  });

  // State for loading and error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  // Add errors state
  const [errors, setErrors] = useState({});

  // Fetch property data
  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/rentProperty/getbyId-rentProperty/${id}`);
        const propertyData = response.data.result;
        
        // Set form data
        setFormData({
          Property_Type: propertyData.Property_Type || '',
          Property_Title: propertyData.Property_Title || '',
          Property_Description: propertyData.Property_Description || '',
          Property_Location: propertyData.Address || '',
          City: propertyData.City || '',
          Rent_Price: propertyData.Rent_Price || '',
          No_of_Bed: propertyData.No_of_Bed || '',
          No_of_Bathrooms: propertyData.No_of_Bathrooms || '',
          No_of_Kitchen: propertyData.No_of_Kitchen || '',
          Floor_Number: propertyData.Floor_Number || '',
          No_of_Floors: propertyData.No_of_Floors || '',
          Contact_Number: propertyData.Contact_Number || '',
          Contact_Name: propertyData.Contact_Name || '',
          Yard: propertyData.Yard || '',
          Marla: propertyData.Marla || '',
          Kanal: propertyData.Kanal || '',
          Acre: propertyData.Acre || '',
        });

        // Set existing images
        if (propertyData.images && propertyData.images.length > 0) {
          setExistingImages(propertyData.images);
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to fetch property data. Please try again.');
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, [id]);

  // Handle file drop
  const onDrop = (acceptedFiles) => {
    setSelectedFiles([...selectedFiles, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxSize: 5242880, // 5MB
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Update validateForm function
  const validateForm = () => {
    const newErrors = {};

    // Validate Property Type
    if (!formData.Property_Type) {
      newErrors.Property_Type = 'Property Type is required';
    }

    // Validate City
    if (!formData.City) {
      newErrors.City = 'City is required';
    }

    // Validate Price
    if (!formData.Rent_Price) {
      newErrors.Rent_Price = 'Price is required';
    } else if (isNaN(formData.Rent_Price) || formData.Rent_Price <= 0) {
      newErrors.Rent_Price = 'Price must be a positive number';
    }

    // Validate Contact Number
    if (!formData.Contact_Number) {
      newErrors.Contact_Number = 'Contact Number is required';
    } else if (!/^03\d{9}$/.test(formData.Contact_Number)) {
      newErrors.Contact_Number = 'Contact Number must be 11 digits starting with 03';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create FormData object
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Append new images
      selectedFiles.forEach(file => {
        formDataToSend.append('images', file);
      });

      // Make API call to update property
      await axios.put(`${API_BASE_URL}${API_ENDPOINTS.RENT_PROPERTIES}/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/listing/rent');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update property. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Remove selected file
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove existing image
  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  if (loading && !formData.Property_Title) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="Edit Rent Property" subtitle="Update property details" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Property updated successfully! Redirecting...
        </Alert>
      )}

      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: colors.primary[400],
          borderRadius: 2,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Property Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select
                  name="Property_Type"
                  value={formData.Property_Type}
                  onChange={handleInputChange}
                  label="Property Type"
                  required
                >
                  <MenuItem value="Residential">Residential</MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Agricultural">Agricultural</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                name="Property_Title"
                value={formData.Property_Title}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="Property_Description"
                value={formData.Property_Description}
                onChange={handleInputChange}
                multiline
                rows={4}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                name="Property_Location"
                value={formData.Property_Location}
                onChange={handleInputChange}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} sm={6}>
              <CityDropdown
                value={formData.City}
                onChange={handleInputChange}
                error={!!errors.City}
                helperText={errors.City}
              />
            </Grid>

            {/* Rent Price */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Rent Price"
                name="Rent_Price"
                value={formData.Rent_Price}
                onChange={handleInputChange}
                type="number"
                error={!!errors.Rent_Price}
                helperText={errors.Rent_Price}
                required
              />
            </Grid>

            {/* Contact Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="Contact_Number"
                value={formData.Contact_Number}
                onChange={handleInputChange}
                required
                placeholder="03XXXXXXXXX"
                inputProps={{
                  maxLength: 11,
                  pattern: '^03\\d{9}$'
                }}
              />
            </Grid>

            {/* Contact Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name"
                name="Contact_Name"
                value={formData.Contact_Name}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Number of Beds */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Number of Beds"
                name="No_of_Bed"
                value={formData.No_of_Bed}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Number of Bathrooms */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Number of Bathrooms"
                name="No_of_Bathrooms"
                value={formData.No_of_Bathrooms}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Number of Kitchens */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Number of Kitchens"
                name="No_of_Kitchen"
                value={formData.No_of_Kitchen}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Floor Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Floor Number"
                name="Floor_Number"
                value={formData.Floor_Number}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Number of Floors */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Floors"
                name="No_of_Floors"
                value={formData.No_of_Floors}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Yard */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Yard"
                name="Yard"
                value={formData.Yard}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Marla */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Marla"
                name="Marla"
                value={formData.Marla}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Kanal */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kanal"
                name="Kanal"
                value={formData.Kanal}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Acre */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Acre"
                name="Acre"
                value={formData.Acre}
                onChange={handleInputChange}
                type="number"
              />
            </Grid>

            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Property Images
              </Typography>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Existing Images
                  </Typography>
                  <Grid container spacing={2}>
                    {existingImages.map((image, index) => (
                      <Grid item key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: 150,
                            height: 150,
                            border: `1px solid ${colors.grey[300]}`,
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={`${API_BASE_URL}/uploads/${image}`}
                            alt={`Property ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeExistingImage(index)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* New Images */}
              <Box
                {...getRootProps()}
                sx={{
                  border: `2px dashed ${colors.grey[300]}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: colors.primary[500],
                  },
                }}
              >
                <input {...getInputProps()} />
                <Typography>
                  Drag and drop images here, or click to select files
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  (Max file size: 5MB, Supported formats: JPG, JPEG, PNG)
                </Typography>
              </Box>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Selected Files
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedFiles.map((file, index) => (
                      <Grid item key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: 150,
                            height: 150,
                            border: `1px solid ${colors.grey[300]}`,
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Selected ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeFile(index)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/listing/rent')}
                  sx={{
                    color: colors.grey[700],
                    borderColor: colors.grey[700],
                    '&:hover': {
                      borderColor: colors.grey[900],
                      backgroundColor: colors.grey[100],
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: colors.primary[500],
                    '&:hover': {
                      backgroundColor: colors.primary[600],
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Property'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default EditRentProperty; 